import _ from 'underscore'

import { Trace, TraceTick } from '../../domain/Trace'
import { TimelineSpan } from '../../domain/TimelineSpan'

interface TracesFilterResult {
    traces: Trace[]
    hidden: number
    hiddenThreshold: number
}

interface SpanDimensions {
    start: number
    duration: number
}

interface TimelineLayoutResult {
    traces: Trace[]
    spans: TimelineSpan[]
    ticks: TraceTick[]
}

export type TimelineLayoutMode = 'classic' | 'condensed' | 'waterfall'

interface TimelineLayoutLane {
    layoutTickId: string
    end: number
    rowIndex: number
}

interface ActiveLayoutSpan {
    end: number
    rowIndex: number
}

const MAX_REUSE_DISTANCE_FROM_LOWEST_ROW = 5

class TimelineTraceDataTransformer {
    /**
     * Align child traces in the given trace set so the child trace is centered below it's parent trace's root span.
     */
    public alignChildTracesToParentSpans(traces: Trace[]): Trace[] {
        const rootTrace = traces[0]
        const childTraces = traces.slice(1)

        let spanDimensions = this.extractSpanDimensionsFromTrace(rootTrace)

        for (const childTrace of childTraces) {
            const childTraceOffset = this.calculateTraceOffset(childTrace, spanDimensions)

            for (const childTraceSpan of childTrace.spans) {
                childTraceSpan.cs += childTraceOffset

                if (childTraceSpan.e) {
                    for (const localEvent of childTraceSpan.e) {
                        localEvent.cs += childTraceOffset
                    }
                }
            }

            for (const globalEvent of childTrace.globalEvents) {
                globalEvent.ts += childTraceOffset
            }

            /*
             * We need to extract offsets trace by trace because there might be multiple levels of nesting
             * which means a trace's offsets are only final after it has been aligned itself.
             */
            spanDimensions = { ...spanDimensions, ...this.extractSpanDimensionsFromTrace(childTrace) }
        }

        return traces
    }

    public countTraceSpans(traces: Trace[]): number {
        return traces.reduce((spanCount, trace) => spanCount + trace.spans.length, 0)
    }

    public filterFastSpansFromTraces(traces: Trace[], limit: number = 500): TracesFilterResult {
        const initialSpanCount = this.countTraceSpans(traces)

        let threshold = 75
        let runs = 0
        let spanCount = initialSpanCount

        const wallTimeThresholdFilter = (span: TimelineSpan) => span.wt >= threshold

        while (spanCount > limit && runs < 5) {
            for (const trace of traces) {
                trace.spans = trace.spans.filter(wallTimeThresholdFilter)
            }

            runs++
            threshold = threshold * 4
            spanCount = this.countTraceSpans(traces)
        }

        return { traces, hidden: initialSpanCount - spanCount, hiddenThreshold: threshold }
    }

    public sortTracesByStartTime(traces: Trace[]): void {
        traces.sort((a, b) => {
            const aStart = a.spans[0].cs
            const bStart = b.spans[0].cs

            if (aStart < bStart) {
                return -1
            }

            if (aStart > bStart) {
                return 1
            }

            return 0
        })
    }

    public cloneTraceSpanAndEventData(traces: Trace[]): Trace[] {
        return traces.map(trace => {
            const clonedTrace = _.clone(trace)

            clonedTrace.spans = trace.spans.map(span => {
                const clonedSpan = _.clone(span)

                clonedSpan.e = (span.e || []).map(localEvent => _.clone(localEvent))

                return clonedSpan
            })

            clonedTrace.globalEvents = trace.globalEvents.map(_.clone)

            return clonedTrace
        })
    }

    /**
     * Build a waterfall layout for the timeline.
     *
     * In waterfall mode each span gets its own row.
     *
     * In classic mode all spans from the same source tick share one row.
     *
     * In condensed mode rows are grouped per source tick and reused only when:
     * - the previous span on that row has already finished, and
     * - the row is still close enough to the current lowest allocated row.
     *
     * Proximity is measured in vertical rows, not by the number of spans processed since
     * that row was last used. When multiple reusable rows exist for the same source tick,
     * the lowest reusable row is preferred.
     */
    public createWaterfallLayout(traces: Trace[], ticks: TraceTick[], layoutMode: TimelineLayoutMode): TimelineLayoutResult {
        const tracesWithLayout = traces.map(trace => ({ ...trace, spans: [] as TimelineSpan[] }))
        const sourceTicksById = ticks.reduce(
            (carry, tick) => ({
                ...carry,
                [tick.id]: tick,
            }),
            {},
        )
        const seenTickIds = new Set<string>()
        const sortedSpans = traces
            .flatMap((trace, traceIndex) => trace.spans.map(span => ({ span, traceIndex })))
            .sort((a, b) => {
                if (a.span.cs !== b.span.cs) {
                    return a.span.cs - b.span.cs
                }

                return b.span.wt - a.span.wt
            })
        const ticksWithLayout: TraceTick[] = []
        const lanesByTickId: { [tickId: string]: TimelineLayoutLane[] } = {}
        const spansWithLayout: TimelineSpan[] = []
        let activeLayoutSpans: ActiveLayoutSpan[] = []

        for (const { span, traceIndex } of sortedSpans) {
            const spanEnd = span.cs + span.wt
            activeLayoutSpans = activeLayoutSpans.filter(activeSpan => activeSpan.end > span.cs)

            const sourceTickId = span.tick || span.id
            const sourceTick = sourceTicksById[sourceTickId] || {
                id: sourceTickId,
                summary: span.summary || span.name,
                wt: span.wt,
                ct: 1,
            }

            let layoutTickId = span.id

            if (layoutMode === 'classic') {
                layoutTickId = sourceTickId

                if (!seenTickIds.has(layoutTickId)) {
                    seenTickIds.add(layoutTickId)
                    ticksWithLayout.push({ ...sourceTick })
                }
            } else if (layoutMode === 'condensed') {
                const lanes = lanesByTickId[sourceTickId] || []
                const lowestProcessedRowIndex = ticksWithLayout.length - 1
                const deepestActiveAncestorRowIndex = activeLayoutSpans.reduce((deepestAncestorRowIndex, activeSpan) => {
                    if (activeSpan.end < spanEnd) {
                        return deepestAncestorRowIndex
                    }

                    return Math.max(deepestAncestorRowIndex, activeSpan.rowIndex)
                }, -1)
                const reusableLane = lanes
                    .slice()
                    .reverse()
                    .find(
                        lane =>
                            lane.end <= span.cs &&
                            lane.rowIndex > deepestActiveAncestorRowIndex &&
                            lowestProcessedRowIndex - lane.rowIndex < MAX_REUSE_DISTANCE_FROM_LOWEST_ROW,
                    )

                if (reusableLane) {
                    layoutTickId = reusableLane.layoutTickId
                    reusableLane.end = spanEnd
                } else {
                    const laneIndex = lanes.length
                    const rowIndex = ticksWithLayout.length

                    layoutTickId = `${sourceTickId}::row-${laneIndex}`
                    lanes.push({ layoutTickId, end: spanEnd, rowIndex })
                    lanesByTickId[sourceTickId] = lanes
                    ticksWithLayout.push({ ...sourceTick, id: layoutTickId, ct: 1 })
                }
            } else {
                ticksWithLayout.push({ ...sourceTick, id: layoutTickId, wt: span.wt, ct: 1 })
            }

            const layoutSpan = { ...span, tick: layoutTickId }
            const layoutLane =
                layoutMode === 'classic'
                    ? null
                    : layoutMode === 'condensed'
                    ? lanesByTickId[sourceTickId]?.find(lane => lane.layoutTickId === layoutTickId)
                    : null
            const rowIndex =
                layoutMode === 'classic'
                    ? ticksWithLayout.findIndex(tick => tick.id === layoutTickId)
                    : layoutMode === 'waterfall'
                    ? ticksWithLayout.length - 1
                    : layoutLane?.rowIndex

            spansWithLayout.push(layoutSpan)
            tracesWithLayout[traceIndex].spans.push(layoutSpan)

            if (typeof rowIndex === 'number') {
                activeLayoutSpans.push({ end: spanEnd, rowIndex })
            }
        }

        return { traces: tracesWithLayout, spans: spansWithLayout, ticks: ticksWithLayout }
    }

    /**
     * Find the correct time offset for the given trace based on the given set of potential parent span dimensions
     */
    private calculateTraceOffset(trace: Trace, spanDimensions: { [key: string]: SpanDimensions }): number {
        if (!trace.parentSpanId) {
            return 0
        }

        const childTraceRootSpan = trace.spans[0]
        const parentSpanDimensions = spanDimensions[trace.parentSpanId]

        const durationDifference = Math.max(parentSpanDimensions.duration - childTraceRootSpan.wt, 0)

        return parentSpanDimensions.start + durationDifference / 2
    }

    private extractSpanDimensionsFromTrace(trace: Trace): { [key: string]: SpanDimensions } {
        const spanDimensions = {}

        for (const span of trace.spans) {
            if (span.spanId) {
                spanDimensions[span.spanId] = {
                    start: span.cs,
                    duration: span.wt,
                }
            }
        }

        return spanDimensions
    }
}

export default TimelineTraceDataTransformer
